import { h, Component } from 'preact';
import Store from 'store/store';
import * as actions from 'store/actions';

import { joinClasses, parseDate, triggerAnim } from 'utils/utils';
import { showTrailerModal } from 'utils/trailer';
import { clamp } from 'utils/math';
import formatDateTime from 'utils/formatDateTime';
import { getButtons } from 'utils/buttons';
import { isVideoAsset, getYoutubeId, isYoutubeLink } from 'utils/assets';

import Play from 'components/icons/playButton';
import Pause from 'components/icons/pauseButton';
import ArrowLeft from 'components/icons/arrowLeft';
import ArrowRight from 'components/icons/arrowRight';

import s from 'components/hero/hero.sss';

// Handle file load errors
const loadErrors = new Set();
async function checkFileExists(url) {
	try {
		const response = await fetch(url, { method: 'HEAD', crossOrigin: 'anonymous' });
		return response.ok;
	} catch (err) {
		// For generic errors, assume the file exists (could be any number of reasons, only 4xx are real missing file errors for our use case)
		return true;
	}
}

export default class Hero extends Component {

	constructor(props) {
		super(props);
		this.state = {
			playing: true,
			// selectedMovie: this.movies[0]
		};
		this.updateMovies(props.movies);

		this.controlsRef = this.controlsRef.bind(this);
		this.onProgressAnimationEnd = this.onProgressAnimationEnd.bind(this);
		this.onTimeUpdate = this.onTimeUpdate.bind(this);
		this.setCurrentProgressElement = this.setCurrentProgressElement.bind(this);
		this.togglePlay = this.togglePlay.bind(this);
		this.selectMovie = this.selectMovie.bind(this);
		this.goToPrevious = this.goToPrevious.bind(this);
		this.goToNext = this.goToNext.bind(this);
		this.onTouchStart = this.onTouchStart.bind(this);
		this.onTouchMove = this.onTouchMove.bind(this);
		this.onTouchEnd = this.onTouchEnd.bind(this);
		this.showTrailer = this.showTrailer.bind(this);
		this.trackHeroButtonClick = this.trackHeroButtonClick.bind(this);
		this.onPreviousClick = this.onPreviousClick.bind(this);
		this.onNextClick = this.onNextClick.bind(this);
		this.renderSelectedMovieButton = this.renderSelectedMovieButton.bind(this);

	}

	componentDidMount() {
		this.observer = new IntersectionObserver(this.onIntersectionUpdate, { threshold: 1 });
		this.observer.observe(this.base);

		// Fix autoplay when remounting
		this.base.querySelector('video[autoplay]')?.play();
	}

	componentWillUnmount() {
		this.observer?.disconnect();
		document.documentElement.classList.remove('hero-top');
	}

	componentWillReceiveProps(props) {
		if (props.movies !== this.props.movies) {
			this.checkAllVideosAndStills(props.movies);
			this.updateMovies(props.movies);
		}
	}

	componentDidUpdate(prevProps, prevState) {
		if (prevState.selectedMovie !== this.state.selectedMovie) {
			let video = this.base.querySelector('video');
			if (video) video.currentTime = 0;
			const nextShowing = this.base.querySelector(`.${s.nextShowing}`);
			if (nextShowing) triggerAnim(nextShowing, s.animate);
		}
		this.updateProgress();
	}

	async checkAllVideosAndStills(data) {
		if (!data) return;
		const allAssets = data.flatMap(item => {
			const heroData = item?.movie?.hero;
			const video = heroData?.video?.url;
			const still = heroData?.still?.url || heroData?.poster;
			return [video, still];
		}).filter(Boolean);
		const results = await Promise.all(allAssets.map(async asset => {
			// Return true because no need to update movies if the error is already known
			if (loadErrors.has(asset)) return true;
			// s3 only returns CORS headers for anonymous requests (with Origin request header)
			//   so if the asset is cached by the browser (from a non-cors request), it will not have the correct headers and fail
			//   add a query string to avoid using potential cached version
			const safeUrl = new URL(asset);
			safeUrl.searchParams.set('hub-co', '1');
			const exists = await checkFileExists(safeUrl.href);
			if (!exists) loadErrors.add(asset);
			return exists;
		}));
		if (results.some(e => !e)) {
			this.updateMovies(data);
		}
	}

	getMoviesList(raw = this.props.movies) {
		return raw.filter(e => {
			const heroData = e?.movie?.hero;
			const video = heroData?.video?.url;
			const still = heroData?.still?.url || heroData?.poster;
			return (video || still) && !loadErrors.has(video) && !loadErrors.has(still);
		});
	}

	updateMovies(raw = this.props.movies) {
		const movies = this.getMoviesList(raw);
		if (this.movies && movies.length === this.movies.length && movies.every((e, i) => e.movie.id === this.movies[i]?.movie?.id)) {
			return;
		}
		this.movies = movies;
		const selectedMovie = this.movies.find(movie => movie.id === this.state.selectedMovie?.id) || this.movies[0];
		// NOTE: might need to delay selectMovie until after render (componentDidUpdate) to make sure scrolling the active item into view works
		this.selectMovie(selectedMovie);
	}

	shouldUseVideo(movie) {
		if (!movie) return false;
		const video = movie.hero?.video?.url;
		return !!video && movie.hero?.type !== 'image' && !loadErrors.has(video);
	}

	onIntersectionUpdate(entries) {
		const fullView = entries.at(-1).isIntersecting;
		if (fullView) {
			document.documentElement.classList.add('hero-top');
		} else {
			document.documentElement.classList.remove('hero-top');
		}
	}

	onProgressAnimationEnd(e) {
		if (e.animationName === s.staticProgress) {
			this.goToNext();
		}
	}

	onTimeUpdate(e) {
		if (!e.target.parentElement) {
			// Has been removed from the DOM, ignore
			return;
		}
		this.updateProgress(e.target);
	}

	setCurrentProgressElement(e) {
		if (e) {
			this.$currentProgress = e;
		} else {
			this.progress = 0;
		}
	}

	updateProgress(video = this.base.querySelector('video')) {
		if (!video || !this.$currentProgress) return;
		let progress = video.currentTime / video.duration;
		this.$currentProgress.classList.remove(s.animate);
		this.$currentProgress.style.setProperty('--progress', progress);
		if (!video.paused) {
			let remaining = video.duration - video.currentTime;
			this.$currentProgress.offsetWidth;
			this.$currentProgress.style.setProperty('--remaining', remaining);
			this.$currentProgress.classList.add(s.animate);
		}
	}

	pause() {
		this.base.querySelector('video')?.pause();
		this.setState({ playing: false });
	}

	play() {
		this.base.querySelector('video')?.play();
		this.setState({ playing: true });
	}

	togglePlay() {
		Store.emit(actions.HERO_VIDEO_SLIDE_PLAY_TOGGLE, { state: !this.state.playing, count: this.movies.length });
		if (this.state.playing) {
			this.pause();
		} else {
			this.play();
		}
	}

	selectMovie(movie) {
		if (movie === this.state.selectedMovie) return;

		this.setState({ selectedMovie: movie });

		const list = this.base?.querySelector(`.${s.list}`);
		const item = list?.children[this.movies.indexOf(movie)];

		if (item) {
			let itemPosition = item.offsetLeft;
			const newScroll = clamp(list.scrollLeft, itemPosition - list.clientWidth + item.clientWidth, itemPosition);
			// Can't use scrollIntoView as it causes the body to scroll (really tries to put the item in the viewport)
			//   see https://github.com/w3c/csswg-drafts/issues/9452
			list.scrollTo({ left: newScroll, behavior: 'smooth' });
		}
	}

	goToPrevious() {
		const selectedIndex = this.movies.findIndex(item => item?.movie?.id === this.state.selectedMovie?.movie?.id);
		const newIndex = selectedIndex === -1 ? -1 : (selectedIndex - 1) % this.movies.length;
		this.selectMovie(this.movies.at(newIndex));
	}

	goToNext() {
		const selectedIndex = this.movies.findIndex(item => item?.movie?.id === this.state.selectedMovie?.movie?.id);
		const newIndex = selectedIndex === -1 ? 0 : (selectedIndex + 1) % this.movies.length;
		this.selectMovie(this.movies.at(newIndex));
	}

	getTouchInfo(e) {
		const touch = e.touches?.[0] || e;
		return { at: performance.now(), x: touch.clientX, y: touch.clientY };
	}
	onTouchStart(e) {
		if (this.$controls?.contains(e.target)) {
			return;
		}
		this.swipe = { current: this.getTouchInfo(e) };
	}

	onTouchMove(e) {
		if (!this.swipe) return;
		const newData = this.getTouchInfo(e);
		// Make sure that the time difference is never 0 (should always be the case, just extra safety)
		if (newData.at !== this.swipe.current.at) {
			this.swipe.previous = this.swipe.current;
			this.swipe.current = newData;
		}
	}

	onTouchEnd() {
		if (!this.swipe) return;
		// Logic vaguely based on zingtouch: https://github.com/zingchart/zingtouch/blob/master/src/gestures/Swipe.js
		const { current, previous } = this.swipe;
		this.swipe = null;
		if (!previous) return;
		if (performance.now() - current.at > 100) {
			// If the finger rested for too long, not a swipe
			return;
		}
		const velocity = Math.hypot(current.x - previous.x, current.y - previous.y) / (current.time - previous.time);
		if (velocity < .2) {
			// If the finger moved too slowly, not a swipe
			return;
		}
		const angle = Math.atan2(current.y - previous.y, current.x - previous.x);
		const cos = Math.cos(angle);
		if (Math.abs(cos) < 0.5) {
			// If the swipe is not horizontal enough, ignore it
			return;
		}
		this[cos > 0 ? 'goToPrevious' : 'goToNext']();
	}

	async showTrailer(url, link, e) {
		e?.preventDefault?.();
		const heroPlaying = this.state.playing;
		if (heroPlaying) this.pause();
		this.trackHeroButtonClick(e);
		await showTrailerModal({ link }, url);
		if (heroPlaying) this.play();
	}

	getDateLabel(item = this.state.selectedMovie) {
		const movie = item?.movie;
		if (!movie) return '';
		const releaseDate = parseDate(movie?.dates?.release);
		if (!+releaseDate) return '';
		const now = Date.now();
		if (now > releaseDate) {
			const status = Store.get().movieStatuses?.[movie.id];
			if (status?.homeEnt === 'homeEnt' && status?.theaters === 'unknown') {
				return 'Watch at Home';
			}
			return 'Now Playing In Theaters';
		}
		const mondayBeforeRelease = new Date(+releaseDate);
		mondayBeforeRelease.setDate(mondayBeforeRelease.getDate() - (mondayBeforeRelease.getDay() + 6) % 7);
		if (now >= mondayBeforeRelease) {
			return `In Theaters ${formatDateTime(releaseDate, 'dddd')}`;
		}
		return formatDateTime(releaseDate, 'MMMM d');
	}

	getNextShowtimeLabel(showtime) {
		return showtime?.label || '';
	}

	getNextShowtimesElements(item = this.state.selectedMovie) {
		const data = this.props.nextShowtimes?.[item?.movie?.id];
		if (!data) return null;
		let labels = data.map(this.getNextShowtimeLabel);
		const duplicates = labels.map(label => <div aria-hidden>{label}</div>);
		return labels.map(label => <div>{label}</div>).concat(
			duplicates,
			labels.length === 1 ? duplicates.concat(duplicates) : [],
		);
	}

	trackHeroButtonClick(e) {
		const title = this.state.selectedMovie?.movie?.title;
		const label = e?.target?.textContent;
		Store.emit(actions.HERO_BUTTON_CLICK, { title, label });
	}

	onItemClick(item) {
		this.selectMovie(item);
		Store.emit(actions.HERO_MOVIE_SELECTION_TAB_CLICK, { title: item?.movie?.title });
	}

	onPreviousClick() {
		this.goToPrevious();
		Store.emit('HERO_TAB_DIRECTION_CLICK', { direction: 'left' });
	}
	onNextClick() {
		this.goToNext();
		Store.emit('HERO_TAB_DIRECTION_CLICK', { direction: 'right' });
	}

	controlsRef(e) {
		this.$controls = e;
	}

	renderControls() {
		const playing = this.state.playing;
		return (
			<div class={s.controls} ref={this.controlsRef} key="controls">
				<div class={s.nav}>
					<div class={s.buttons}>
						<button class={s.prev} onClick={this.onPreviousClick} aria-label="Switch to previous movie"><ArrowLeft /></button>
						<button class={s.next} onClick={this.onNextClick} aria-label="Switch to next movie"><ArrowRight /></button>
					</div>
					<div class={s.list}>
						{this.movies.map((item, i) => {
							const movie = item?.movie;
							if (!movie) return null;
							const current = this.state.selectedMovie?.movie?.id === movie.id;
							let progressProps = !current ? {} : {
								ref: this.setCurrentProgressElement,
								onAnimationEnd: this.onProgressAnimationEnd,
							};
							const useVideo = this.shouldUseVideo(movie);
							return (
								<button
									class={joinClasses(s.item, current && s.active)}
									onClick={() => this.onItemClick(item)}
									key={movie.id || i}
								>
									<div class={s.title}>{movie.title}</div>
									<div class={s.date}>{this.getDateLabel(item)}</div>
									<div class={joinClasses(s.progress, !playing && s.paused, !useVideo && s.static)} {...progressProps} />
								</button>
							);
						})}
					</div>
				</div>
				<button class={s.playPause} onClick={this.togglePlay} aria-label={playing ? 'Pause' : 'Play'}>{playing ? <Pause height="14" /> : <Play height="14" />}</button>
			</div>
		);
	}

	renderSelectedMovieButton(button, i, buttons) {
		const label = button?.label;
		if (!label) return null;

		const className = joinClasses(s.button, button.type === 'primary' && s.primary);

		if (isVideoAsset(button.destination)) {
			// Primary link of the item (e.g. get tickets), to also show it in the trailer overlay
			const mainLink = buttons[0] !== button ? buttons[0] : null;
			const onClick = (e) => this.showTrailer(button.destination, mainLink, e);

			// youtube trailers: allow "open in new tab" to work by using a link
			if (isYoutubeLink(button.destination)) {

				// For youtube:id links (while we still support them), transform to proper clickable link
				let link = button.destination;
				if (link.startsWith('youtube:')) link = `https://www.youtube.com/watch?v=${getYoutubeId(link)}`;

				return <a class={className} href={link} target="_blank" rel="noopener noreferrer" onClick={onClick}>{label}</a>;
			}

			return <button class={className} onClick={onClick}>{label}</button>;
		}
		return <a class={className} href={button.destination} target="_blank" rel="noopener noreferrer" onClick={this.trackHeroButtonClick}>{label}</a>;
	}
	renderSelectedMovieDetails() {
		const item = this.state.selectedMovie;
		if (!item?.movie) return null;
		const movie = item.movie;
		const buttonsList = getButtons(movie, item);
		return (
			<div class={s.details} key="details">
				<div class={s.info}>{this.getDateLabel()}</div>
				<h2 class={s.title}>{movie?.title}</h2>
				<div class={joinClasses(s.nextShowing, s.animate)}>
					<div class={s.scroller}>
						{this.getNextShowtimesElements()}
					</div>
				</div>
				<div class={s.buttons}>
					{buttonsList.map(this.renderSelectedMovieButton)}
				</div>
			</div>
		);
	}

	render(props, state) {
		// const video = state.selectedMovie?.heroVideo;
		const activeHeroData = state.selectedMovie?.movie?.hero;
		const video = activeHeroData?.video?.url;
		// Should we use "poster" at all? What is the difference with "still"?
		const still = activeHeroData?.still?.url || activeHeroData?.poster;
		const showVideo = this.shouldUseVideo(state.selectedMovie?.movie);

		return (
			<div class={s.wrapper}>
				<div
					class={s.media}
					onTouchStart={this.onTouchStart}
					onTouchMove={this.onTouchMove}
					onTouchEnd={this.onTouchEnd}
					key="media"
				>
					{showVideo && (
						<video
							class={s.preview}
							src={video}
							poster={still}
							autoplay={this.state.playing}
							preload="auto"
							playsinline
							muted
							onTimeUpdate={this.onTimeUpdate}
							onEnded={this.goToNext}
							width="1920"
							height="800"
							key={video}
						/>
					)}
					<div class={joinClasses(s.videoPoster, (!state.playing || !showVideo) && s.show)} key="poster">
						{!!still && (
							<img
								src={still}
								alt={activeHeroData?.still?.alt || `${state.selectedMovie?.movie?.title} poster image`}
								key={state.selectedMovie?.movie?.id || 'none'}
							/>
						)}
					</div>
					{this.renderControls()}
				</div>
				{this.renderSelectedMovieDetails()}
			</div>
		);
	}
}